import { ReactElement, createContext, useContext, useEffect, useState } from 'react';

import { AdyenCreditCardFormData } from '@hooks/useAdyenState.static';
import { BancontactAdyenCreditCardFormData } from '@hooks/useBancontactAdyenState.static';
import { useTranslation } from '@hooks/useTranslations';
import {
  PSP,
  deleteAllCreditCards,
  deleteCreditCard,
  selectCreditCard,
  storeCreditCard,
  BaseCreditCardRequestParameters,
  StoreCardData,
  BaseCreditCardRequestParamsPM,
} from '@services/creditCardService';
import { CreditCardData } from '@types-internal';
import { TokenizeCardResult } from '@types-internal/BraintreeTypes';
import { deleteCardArrayAndUpdate, selectCardAndUnselectPrevious } from '@utils/cardUtils';

import { useAppContext } from './AppContextProvider';
import { useConfig } from './ConfigProvider';
import { OverlayIds, useOverlays } from './OverlaysProvider';

export interface CreditCardsMutationData {
  btToken?: string;
  cardToDelete?: CreditCardData;
  creditCards: CreditCardData[];
}

interface ContextValues {
  initiateCardDeletion: (creditCard: CreditCardData) => void;
  deleteCard: () => Promise<void>;
  saveCreditCard: (tokenizationResult: TokenizeCardResult) => Promise<boolean>;
  saveAdyenCreditCard: (adyenFormData: AdyenCreditCardFormData) => Promise<boolean>;
  saveBancontactAdyenCreditCard: (
    bancontactAdyenFormData: BancontactAdyenCreditCardFormData,
  ) => Promise<boolean>;
  deleteAllCards: () => Promise<boolean>;
  makeCreditCardActive: (creditCard: CreditCardData) => Promise<boolean>;
  data: CreditCardsMutationData;
}

const CreditCardsMutationContext = createContext<ContextValues>({
  initiateCardDeletion: () => null,
  saveCreditCard: () => Promise.resolve(false),
  saveAdyenCreditCard: () => Promise.resolve(false),
  saveBancontactAdyenCreditCard: () => Promise.resolve(false),
  deleteCard: () => Promise.resolve(),
  deleteAllCards: () => Promise.resolve(false),
  makeCreditCardActive: () => Promise.resolve(false),
  data: { creditCards: [] },
});

interface CreditCardsMutationProps {
  pspToken: string;
  psp: PSP;
  creditCards: CreditCardData[];
  paymentMethod: BaseCreditCardRequestParamsPM;
}

/**
 * Manages the list of credit cards of the customer, by proposing to the other components the methods to select, delete and add credit cards.
 * Each method will call the appropriate method from the creditCardService to send a request to the backend.
 */
const CreditCardsMutationProvider: React.FC<CreditCardsMutationProps> = ({
  children,
  pspToken,
  psp,
  creditCards: inputCards,
  paymentMethod,
}): ReactElement => {
  const { t } = useTranslation();
  const { config } = useConfig();
  const { csrfToken } = useAppContext();
  const { showOverlay } = useOverlays();
  const [creditCards, setCreditCards] = useState<CreditCardData[]>(inputCards);
  const [cardToDelete, setCartToDelete] = useState<CreditCardData>();

  const initiateCardDeletion = (card: CreditCardData) => {
    setCartToDelete(card);
    showOverlay(OverlayIds.DELETE_CARD);
  };

  const makeCreditCardActive = async (creditCard: CreditCardData) => {
    // Optimistic UI: we display the card as selected before actually making the request
    const previousSelectedCard = creditCards.find((c) => c.selected);
    setCreditCards((currentCreditCards) =>
      selectCardAndUnselectPrevious(currentCreditCards, creditCard.pspIdentifier),
    );

    function revertOptimisticUI() {
      setCreditCards((currentCreditCards) =>
        selectCardAndUnselectPrevious(
          currentCreditCards,
          previousSelectedCard ? previousSelectedCard.pspIdentifier : null,
        ),
      );
    }

    try {
      const isSuccess = await selectCreditCard({
        csrfToken: csrfToken as string, // todo: how to be sure we do have a string and never undefined?
        creditCardIdentifier: creditCard.pspIdentifier,
        psp,
        paymentMethod,
      });

      if (!isSuccess) {
        revertOptimisticUI();
      }
      return isSuccess;
    } catch (error) {
      // just catch the error in passing, to ensure to revert the UI since the card could not be selected
      revertOptimisticUI();
      throw error;
    }
  };

  /**
   * Store the Braintree Credit Card data in a checkout session.
   *
   * @param tokenizationResult The Braintree Card tokenization result.
   * @return The boolean value indicate if this operation is successful.
   */
  const saveCreditCard = async (tokenizationResult: TokenizeCardResult): Promise<boolean> => {
    if (tokenizationResult.success) {
      try {
        const { data } = tokenizationResult;
        const body: StoreCardData = {
          braintreeNonce: data.nonce,
          braintreeLastFourNumber: data.details.lastFour,
          braintreeCardholderName: data.details.cardholderName,
          braintreeExpirationMonth: data.details.expirationMonth,
          braintreeExpirationYear: data.details.expirationYear,
          braintreeCardType: data.details.cardType,
          braintreeStoreCard: data.storeInVault,
          braintreeBinData: data.binData,
          braintreeBin: data.details.bin,
        };
        const params: BaseCreditCardRequestParameters = {
          csrfToken: csrfToken as string,
          paymentMethod: 'CREDIT_CARD',
          psp: 'BRAINTREE',
        };
        await storeCreditCard(params, body);
        if (config.env !== 'local') {
          window.location.assign('/checkout/preview');
        }
        return true;
      } catch (error) {
        throw new Error(t('checkout.payment.creditcard.error.addcard.apicall'));
      }
    } else if (tokenizationResult.errorDisplayable) {
      throw new Error(t('checkout.payment.creditcard.error.addcard.apicall'));
    } else {
      return false;
    }
  };

  /**
   * Store the Adyen Credit Card data in a checkout session.
   */
  const saveAdyenCreditCard = (data: AdyenCreditCardFormData): Promise<boolean> => {
    const params: BaseCreditCardRequestParameters = {
      csrfToken: csrfToken as string,
      psp: 'ADYEN',
      paymentMethod: 'CREDIT_CARD',
    };
    return storeCreditCard(params, data);
  };

  /**
   * Store the Bancontact Adyen Credit Card data in a checkout session.
   */
  const saveBancontactAdyenCreditCard = (
    data: BancontactAdyenCreditCardFormData,
  ): Promise<boolean> => {
    const params: BaseCreditCardRequestParameters = {
      csrfToken: csrfToken as string,
      paymentMethod: 'BANCONTACT',
      psp: 'ADYEN',
    };

    return storeCreditCard(params, data);
  };

  const deleteCard = async () => {
    if (!cardToDelete) {
      return;
    }

    const newSelectedCardPspIdentifier = await deleteCreditCard({
      csrfToken: csrfToken as string,
      creditCardIdentifier: cardToDelete.pspIdentifier,
      paymentMethod,
      psp,
    });

    // Remove deleted and update selection if changed
    const updatedCards = deleteCardArrayAndUpdate(
      creditCards,
      cardToDelete,
      newSelectedCardPspIdentifier,
    );
    setCreditCards(updatedCards);
  };

  const deleteAllCards = async () => {
    const isSuccess = await deleteAllCreditCards({
      csrfToken: csrfToken as string,
      psp,
      paymentMethod,
    });
    // if (isSuccess) {
    setCreditCards([]);
    // }
    return isSuccess;
  };

  useEffect(() => {
    setCreditCards(inputCards);
  }, [inputCards]);

  return (
    <CreditCardsMutationContext.Provider
      value={{
        initiateCardDeletion,
        deleteCard,
        deleteAllCards,
        makeCreditCardActive,
        saveCreditCard,
        saveBancontactAdyenCreditCard,
        saveAdyenCreditCard,
        data: {
          btToken: pspToken,
          cardToDelete,
          creditCards,
        },
      }}>
      {children}
    </CreditCardsMutationContext.Provider>
  );
};

export const useCreditcardService = () => {
  return useContext(CreditCardsMutationContext);
};

export default CreditCardsMutationProvider;
